/* eslint-disable react/no-unused-prop-types */
import { Box } from '@mui/material';
import DeleteModal from 'components/delete-modal';
import { UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { toNumber } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { unlinkAllFunctionsAction } from 'store/actions/employee-function-link-actions';
import {
  DATAGRID__NO_ROWS,
  EMPLOYEE_FUNCTION_LINKS__COLUMN_CATEGORIES,
  EMPLOYEE_FUNCTION_LINKS__COLUMN_EMPLOYEE,
  EMPLOYEE_FUNCTION_LINKS__COLUMN_FUNCTIONS,
  EMPLOYEE_FUNCTION_LINKS__REMOVE_ROW_CONFIRM,
} from 'translations/constants';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/DeleteGrey.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/EditGrey.svg';
import { EmployeeFunctionLinksGridProps, IEmployeeFunctionLinkRow } from './types';

const EmployeeFunctionLinksGrid = (props: EmployeeFunctionLinksGridProps): JSX.Element => {
  const { rows, canManage, onEdit } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const [employeeIdToRemove, setEmployeeIdToRemove] = useState<number>();

  const getRowId = useCallback((row: IEmployeeFunctionLinkRow) => row.employeeId, []);

  const actionsCellRenderer = useCallback(
    ({ row }: { row: IEmployeeFunctionLinkRow }) =>
      canManage ? (
        <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => onEdit(row)}>
            <EditIcon />
          </Box>
          <Box sx={{ cursor: 'pointer' }} onClick={() => setEmployeeIdToRemove(row.employeeId)}>
            <DeleteIcon />
          </Box>
        </Box>
      ) : null,
    [canManage, onEdit],
  );

  const columns = useMemo<UPTanStackTableColumn<IEmployeeFunctionLinkRow>[]>(
    () => [
      {
        id: 'employeeName',
        header: t(EMPLOYEE_FUNCTION_LINKS__COLUMN_EMPLOYEE),
        accessorKey: 'employeeName',
        size: 240,
      },
      {
        id: 'functionsLabel',
        header: t(EMPLOYEE_FUNCTION_LINKS__COLUMN_FUNCTIONS),
        accessorKey: 'functionsLabel',
        size: 320,
      },
      {
        id: 'categoriesLabel',
        header: t(EMPLOYEE_FUNCTION_LINKS__COLUMN_CATEGORIES),
        accessorKey: 'categoriesLabel',
        size: 240,
      },
      { id: 'actions', header: '', size: 100, align: 'center', headerAlign: 'center', cell: actionsCellRenderer },
    ],
    [t, actionsCellRenderer],
  );

  return (
    <>
      <UPTanStackTable
        data={rows}
        columns={columns}
        getRowId={getRowId}
        enableSorting={false}
        emptyMessage={t(DATAGRID__NO_ROWS)}
        sx={{ height: 'auto', '& .MuiTableContainer-root': { maxHeight: '500px' } }}
      />
      {employeeIdToRemove !== undefined && (
        <DeleteModal
          open
          content={t(EMPLOYEE_FUNCTION_LINKS__REMOVE_ROW_CONFIRM)}
          onClose={() => setEmployeeIdToRemove(undefined)}
          onDelete={() =>
            dispatch(unlinkAllFunctionsAction({ companyId: toNumber(companyId), employeeId: employeeIdToRemove }))
          }
        />
      )}
    </>
  );
};

export default EmployeeFunctionLinksGrid;
